import React, { createContext, useContext, useReducer, useEffect, ReactNode } from 'react';
import APIService, { ClienteRegisterForm } from '../services/api';

interface AuthState {
  user: any | null;
  isLoading: boolean;
  isAuthenticated: boolean;
}

type AuthAction =
  | { type: 'LOADING' }
  | { type: 'LOGIN_SUCCESS'; payload: any }
  | { type: 'LOGOUT' }
  | { type: 'STOP_LOADING' };

const initialState: AuthState = {
  user: null,
  isLoading: true,
  isAuthenticated: false,
};

const authReducer = (state: AuthState, action: AuthAction): AuthState => {
  console.log('🔄 AuthReducer: Action dispatched:', action.type);
  
  switch (action.type) {
    case 'LOADING':
      return { ...state, isLoading: true };
    case 'LOGIN_SUCCESS':
      return {
        ...state,
        user: action.payload,
        isAuthenticated: true,
        isLoading: false,
      };
    case 'LOGOUT':
      console.log('🔄 AuthReducer: Processing LOGOUT action');
      return {
        ...state,
        user: null,
        isAuthenticated: false,
        isLoading: false,
      };
    case 'STOP_LOADING':
      return { ...state, isLoading: false };
    default:
      return state;
  }
};

const AuthContext = createContext<any>(undefined);

export { AuthContext };

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider = ({ children }: AuthProviderProps): JSX.Element => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // Check for stored authentication on app start
  useEffect(() => {
    checkStoredAuth();
  }, []);

  const checkStoredAuth = async () => {
    try {
      console.log('🔐 AuthContext: Iniciando verificación de autenticación...');
      dispatch({ type: 'LOADING' });
      
      const { token } = await APIService.getStoredAuthData();
      console.log('🔐 AuthContext: Token encontrado:', token ? 'Sí' : 'No');
      
      if (token) {
        console.log('🔐 AuthContext: Token encontrado, validando con base de datos...');
        
        try {
          // ✅ SIEMPRE VALIDAR CON LA BASE DE DATOS - NUNCA CONFIAR EN CACHÉ
          const currentUser = await APIService.getCurrentUser();
          console.log('✅ AuthContext: Usuario verificado en BD:', currentUser.name, 'ID:', currentUser.cliente_id || currentUser.id);
          dispatch({ type: 'LOGIN_SUCCESS', payload: currentUser });
        } catch (validationError) {
          console.warn('⚠️ AuthContext: Token inválido o usuario no existe en BD, limpiando caché');
          console.error('❌ Error de validación:', validationError);
          await APIService.logout();
          dispatch({ type: 'STOP_LOADING' });
        }
      } else {
        console.log('🔐 AuthContext: No hay token, usuario debe hacer login');
        // Limpiar cualquier dato residual
        await APIService.logout();
        dispatch({ type: 'STOP_LOADING' });
      }
    } catch (error) {
      console.error('🔐 AuthContext: Error verificando autenticación:', error);
      await APIService.logout();
      dispatch({ type: 'STOP_LOADING' });
    }
  };

  const login = async (credentials: { login: string; password: string }): Promise<void> => {
    try {
      dispatch({ type: 'LOADING' });
      console.log('🔐 AuthContext: Intentando login con:', credentials.login);
      // Paso 1: Intentar login en el backend
      const { user, token } = await APIService.login(credentials);
      console.log('✅ AuthContext: Login exitoso en backend, token recibido');
      // Paso 2: VALIDAR INMEDIATAMENTE que el usuario existe en la BD
      console.log('🔍 AuthContext: Validando usuario en base de datos...');
      const verifiedUser = await APIService.getCurrentUser();
      console.log('✅ AuthContext: Usuario verificado en BD:', verifiedUser.name, 'ID:', verifiedUser.cliente_id || verifiedUser.id);
      // Paso 3: Solo entonces permitir el login
      dispatch({ type: 'LOGIN_SUCCESS', payload: verifiedUser });
    } catch (error: any) {
      dispatch({ type: 'STOP_LOADING' });
      console.error('❌ AuthContext: Error en login:', error);
      console.error('❌ AuthContext: Error message:', error.message);
      console.error('❌ AuthContext: Response data:', error.response?.data);
      console.error('❌ AuthContext: Status code:', error.response?.status);
      // Limpiar cualquier dato residual si el login falla
      await APIService.logout();
      const errorMessage = error instanceof Error ? error.message : 'Login failed';
      throw new Error(errorMessage);
    }
  };

  const register = async (clienteData: ClienteRegisterForm): Promise<void> => {
    try {
      dispatch({ type: 'LOADING' });
      const result = await APIService.register(clienteData);
      console.log('📝 AuthContext: Registro exitoso, usuario creado:', result.user.name);
      // NO hacer login automático, solo completar el registro
      dispatch({ type: 'STOP_LOADING' });
      // El usuario debe hacer login manualmente con sus credenciales
    } catch (error: any) {
      dispatch({ type: 'STOP_LOADING' });
      console.error('📝 AuthContext: Error de registro completo:', error);
      console.error('📝 AuthContext: Error response:', error.response?.data);
      console.error('📝 AuthContext: Validation errors:', error.response?.data?.errors);
      
      // Construir mensaje detallado con los errores de validación
      let errorMessage = error.response?.data?.message || error.message || 'Registration failed';
      
      if (error.response?.data?.errors) {
        const validationErrors = Object.entries(error.response.data.errors)
          .map(([field, messages]: [string, any]) => `${field}: ${Array.isArray(messages) ? messages.join(', ') : messages}`)
          .join(' | ');
        errorMessage = `${errorMessage} - ${validationErrors}`;
      }
      
      throw new Error(errorMessage);
    }
  };

  const logout = async (): Promise<void> => {
    try {
      console.log('🔐 AuthContext: Logout initiated...');
      await APIService.logout();
      console.log('🔐 AuthContext: API logout completed');
    } catch (error) {
      console.error('🔐 AuthContext: Logout error:', error);
    } finally {
      console.log('🔐 AuthContext: Dispatching LOGOUT action');
      dispatch({ type: 'LOGOUT' });
      console.log('🔐 AuthContext: Logout completed');
    }
  };

  const refreshToken = async (): Promise<void> => {
    try {
      // await APIService.refreshToken(); // TODO: Implementar refreshToken
      console.log('🔄 AuthContext: RefreshToken not implemented yet');
    } catch (error) {
      console.error('Token refresh failed:', error);
      // If refresh fails, logout user
      await logout();
    }
  };

  const updateUser = (clienteData: any): void => {
    if (state.user) {
      const updatedCliente = {
        ...state.user,
        ...clienteData,
      };
      dispatch({ type: 'LOGIN_SUCCESS', payload: updatedCliente });
    }
  };

  const contextValue = {
    user: state.user,
    isLoading: state.isLoading,
    isAuthenticated: state.isAuthenticated,
    login,
    register,
    logout,
    updateUser,
    refreshToken,
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
